import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../api";

const AuthContext = createContext(null);

const persist = (data) => {
  if (data?.token) localStorage.setItem("token", data.token);
  if (data?.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
  if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));
};

const clear = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("user");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const verify = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
    } catch {
      setUser(null);
      clear();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  const login = async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    persist(data);
    setUser(data.user);
    return data.user;
  };

  const signup = async (form) => {
    const { data } = await api.post("/api/auth/signup", form);
    persist(data);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignore */
    }
    clear();
    setUser(null);
  };

  const updateProfile = async (body) => {
    const { data } = await api.put("/api/auth/profile", body);
    const updated = data.user || { ...user, ...body };
    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));
    return updated;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
